/**
 * UnifiedDailyGrid — TRANSPOSED unified daily pivot for an F&C activity.
 *
 * Rows    (time)  · Month headers → click to expand daily rows.
 * Columns (metric) · Qty Plan | Qty Fcst | Qty Actl | MP:Trade P/F/A | EQ:Type P/F/A
 *
 * MP/EQ daily values are proportionally distributed against the
 * corresponding quantity distribution so a resource shows on the SAME
 * dates as its phase quantity:
 *
 *   mpPlan[date]  = totalMP * (planQty[date]  / totalPlanQty)
 *   mpFcst[date]  = totalMP * (fcstQty[date]  / totalFcstQty)
 *   mpActl[date]  = totalMP * (actlQty[date]  / totalActlQty)
 */
import { useState, useEffect, useMemo, Fragment } from 'react';
import api from '@/lib/api';
import { Loader2, ChevronDown, ChevronRight } from 'lucide-react';

// ────────────────────────────────────────────────────────
// Utility
// ────────────────────────────────────────────────────────
const ymKey = (d) => (d || '').slice(0, 7);
const iso = (d) => (d || '').slice(0, 10);

// Builds a Map<date, value> and HARD-CLAMPS it to [lo, hi] so period buckets
// that drifted outside the activity's own date window (e.g. legacy 2025 rows
// left behind by a date cascade) can never render in the grid.
function toMap(rows, dateKey = 'periodId', valKey = 'mtdPlanQty', lo = null, hi = null) {
  const m = new Map();
  (rows || []).forEach((r) => {
    const k = iso(r[dateKey]);
    if (!k) return;
    if (lo && k < lo) return;
    if (hi && k > hi) return;
    m.set(k, Number(r[valKey] || 0));
  });
  return m;
}

function totalOf(m) {
  let s = 0;
  m.forEach((v) => (s += v));
  return s;
}

// Distribute a resource total across Qty phase-dates proportionally to
// each day's Qty share.  MP/EQ head-counts are ROUNDED to whole numbers
// (people/machines can't be fractional).  Returns a Map<date, number>.
function distributeProportionally(totalUnits, qtyMap, integer = false) {
  const out = new Map();
  const totalQty = totalOf(qtyMap);
  if (!totalUnits || totalQty <= 0) return out;
  qtyMap.forEach((qty, date) => {
    if (qty > 0) {
      const raw = totalUnits * (qty / totalQty);
      out.set(date, integer ? Math.round(raw) : raw);
    }
  });
  return out;
}

// ────────────────────────────────────────────────────────
// Main component
// ────────────────────────────────────────────────────────
export default function UnifiedDailyGrid({ projectId, activityId, activity }) {
  const [macro, setMacro] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedMonths, setExpandedMonths] = useState({});

  useEffect(() => {
    if (!projectId || !activityId) return;
    setLoading(true);
    api
      .get(`/v1/projects/${projectId}/fc/activities/${activityId}/macro-allocations`)
      .then((r) => setMacro(r.data))
      .catch(() => setMacro(null))
      .finally(() => setLoading(false));
  }, [projectId, activityId]);

  // ── Date windows each phase is allowed to occupy
  const planLo = iso(activity.planStartDate);
  const planHi = iso(activity.planFinishDate);
  const fcstLo = iso(activity.forecastStartDate) || planLo;
  const fcstHi = iso(activity.forecastFinishDate) || planHi;
  const actlLo = iso(activity.actualStartDate) || planLo;
  const actlHi = iso(activity.actualFinishDate) || planHi;

  // ── Per-day Qty values (Plan / Forecast / Actual), clamped to their window
  const planQtyByDay = useMemo(() => toMap(activity.plannedDistribution, 'periodId', 'mtdPlanQty', planLo, planHi), [activity, planLo, planHi]);
  const fcstQtyByDay = useMemo(() => toMap(activity.forecastDistribution, 'periodId', 'mtdPlanQty', fcstLo, fcstHi), [activity, fcstLo, fcstHi]);
  const actlQtyByDay = useMemo(() => toMap(activity.actualProgress, 'periodId', 'mtdActualQty', actlLo, actlHi), [activity, actlLo, actlHi]);

  // Union of dates where any Qty phase has data > 0 — these become the row axis
  const dateRows = useMemo(() => {
    const s = new Set();
    [planQtyByDay, fcstQtyByDay, actlQtyByDay].forEach((m) => {
      m.forEach((v, k) => { if (v > 0) s.add(k); });
    });
    return Array.from(s).sort();
  }, [planQtyByDay, fcstQtyByDay, actlQtyByDay]);

  const monthGroups = useMemo(() => {
    const g = {};
    dateRows.forEach((d) => {
      const m = ymKey(d);
      (g[m] = g[m] || []).push(d);
    });
    return g;
  }, [dateRows]);

  const months = useMemo(() => Object.keys(monthGroups).sort(), [monthGroups]);
  const toggleMonth = (m) => setExpandedMonths((p) => ({ ...p, [m]: !p[m] }));

  // ── Column definitions (metrics)
  //    Qty first, then per-trade MP × P/F/A, then per-type EQ × P/F/A.
  const columns = useMemo(() => {
    const cols = [
      { key: 'qtyPlan', group: 'Qty', label: 'Plan',     color: '#3b82f6', values: planQtyByDay, dp: 2 },
      { key: 'qtyFcst', group: 'Qty', label: 'Forecast', color: '#f59e0b', values: fcstQtyByDay, dp: 2 },
      { key: 'qtyActl', group: 'Qty', label: 'Actual',   color: '#10b981', values: actlQtyByDay, dp: 2 },
    ];

    const mpAllocs = (macro?.allocations || []).filter((a) => a.resourceType === 'LABOR');
    const eqAllocs = (macro?.allocations || []).filter((a) => a.resourceType === 'EQUIPMENT');

    const addResource = (r, prefix, palette) => {
      const totalUnits = (r.daily || []).reduce((s, d) => s + Number(d.qty || 0), 0)
        || Number(r.totalUnitDays) || Number(r.unitsPerDay || 0) * (r.daily?.length || 0);
      const planVals = distributeProportionally(totalUnits, planQtyByDay, true);
      const fcstVals = distributeProportionally(totalUnits, fcstQtyByDay, true);
      const actlVals = distributeProportionally(totalUnits, actlQtyByDay, true);
      const grp = `${prefix} · ${r.resourceName}`;
      const sub = `${r.unitsPerDay?.toFixed?.(1) ?? r.unitsPerDay}/d · Macro ${r.macroTaskCode}`;
      cols.push({ key: `${prefix.toLowerCase()}-${r.resourceId}-plan`, group: grp, sub, label: 'Plan',     color: palette.plan, values: planVals, dp: 0 });
      cols.push({ key: `${prefix.toLowerCase()}-${r.resourceId}-fcst`, group: grp, sub, label: 'Forecast', color: palette.fcst, values: fcstVals, dp: 0 });
      cols.push({ key: `${prefix.toLowerCase()}-${r.resourceId}-actl`, group: grp, sub, label: 'Actual',   color: palette.actl, values: actlVals, dp: 0 });
    };

    mpAllocs.forEach((r) => addResource(r, 'MP', { plan: '#3b82f6', fcst: '#f59e0b', actl: '#10b981' }));
    eqAllocs.forEach((r) => addResource(r, 'EQ', { plan: '#7c3aed', fcst: '#c084fc', actl: '#a855f7' }));

    return cols;
  }, [planQtyByDay, fcstQtyByDay, actlQtyByDay, macro]);

  // Column groups (header row 1) — collapse P/F/A cells under one metric label
  const columnGroups = useMemo(() => {
    const g = [];
    let cur = null;
    columns.forEach((c) => {
      if (!cur || cur.group !== c.group) {
        cur = { group: c.group, sub: c.sub, span: 0 };
        g.push(cur);
      }
      cur.span += 1;
    });
    return g;
  }, [columns]);

  // Per-column total across all rendered rows
  const colTotals = useMemo(() => {
    const m = {};
    columns.forEach((c) => {
      let s = 0;
      c.values.forEach((v) => (s += v));
      m[c.key] = s;
    });
    return m;
  }, [columns]);

  // Per-month per-column totals for the collapsed-month rows
  const monthTotals = useMemo(() => {
    const map = {};
    months.forEach((mo) => {
      map[mo] = {};
      columns.forEach((c) => {
        let s = 0;
        (monthGroups[mo] || []).forEach((d) => (s += c.values.get(d) || 0));
        map[mo][c.key] = s;
      });
    });
    return map;
  }, [months, monthGroups, columns]);

  const fmt = (v, dp) => (v > 0 ? v.toFixed(dp) : '—');

  if (loading && !macro) {
    return (
      <div className="p-3 flex items-center gap-2 text-xs" style={{ color: 'var(--text-muted)' }}>
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading unified daily grid…
      </div>
    );
  }
  if (dateRows.length === 0) {
    return (
      <div className="p-3 text-xs" style={{ color: 'var(--text-muted)' }}>
        No quantity distribution yet. Generate a plan distribution first.
      </div>
    );
  }

  return (
    <div className="rounded-lg border overflow-hidden"
         style={{ borderColor: 'var(--border-color)', background: 'var(--card-bg)' }}
         data-testid={`fc-unified-grid-${activity.activityId}`}>
      <div className="px-3 py-1.5 border-b flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wider"
           style={{ borderColor: 'var(--border-color)', color: 'var(--text-muted)', background: 'var(--card-bg-alt)' }}>
        <span>Unified Daily · rows=Date · cols=Qty / Manpower / Equipment (Plan / Fcst / Actl) — MP/EQ distributed proportionally to Qty</span>
        <span className="ml-auto text-[9px] font-normal normal-case tracking-normal">
          {dateRows.length} days · {columns.length} metric columns
        </span>
      </div>

      <div className="overflow-auto max-h-[620px]">
        <table className="text-[10px] border-collapse" style={{ minWidth: '100%' }}>
          <thead>
            {/* Row 1 — metric group header */}
            <tr style={{ background: 'var(--card-bg)' }}>
              <th className="p-1.5 text-left font-medium sticky left-0 top-0 z-30"
                  style={{ color: 'var(--text-muted)', background: 'var(--card-bg)', minWidth: 140 }}
                  rowSpan={2}>
                Date
              </th>
              {columnGroups.map((g, i) => (
                <th key={`${g.group}-${i}`} colSpan={g.span}
                    className="p-1 text-center font-semibold border-l whitespace-nowrap sticky top-0 z-20"
                    style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)',
                             color: 'var(--text-primary)', minWidth: 44 * g.span }}>
                  {g.group}
                  {g.sub && <div className="text-[9px] font-normal" style={{ color: 'var(--text-muted)' }}>{g.sub}</div>}
                </th>
              ))}
            </tr>
            {/* Row 2 — phase (Plan / Forecast / Actual) sub-header */}
            <tr style={{ background: 'var(--card-bg-alt)' }}>
              {columns.map((c) => (
                <th key={c.key}
                    className="p-1 text-center font-medium border-l whitespace-nowrap sticky z-20"
                    style={{ background: 'var(--card-bg-alt)', borderColor: 'var(--border-color)',
                             color: c.color, minWidth: 60, top: 30 }}
                    data-testid={`fc-unified-col-${c.key}`}>
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {/* Grand Total row */}
            <tr className="border-t font-semibold" style={{ borderColor: 'var(--border-color)', background: 'var(--card-bg)' }}>
              <td className="p-1.5 sticky left-0 z-10" style={{ background: 'var(--card-bg)', color: 'var(--text-primary)' }}>Total</td>
              {columns.map((c) => (
                <td key={c.key} className="p-1.5 text-right font-mono border-l"
                    style={{ color: c.color, borderColor: 'var(--border-color)' }}>
                  {fmt(colTotals[c.key] || 0, c.dp)}
                </td>
              ))}
            </tr>

            {months.map((mo) => {
              const isOpen = expandedMonths[mo];
              const dayCount = (monthGroups[mo] || []).length;
              return (
                <Fragment key={mo}>
                  {/* Month rollup row (clickable) */}
                  <tr className="border-t cursor-pointer hover:opacity-80"
                      onClick={() => toggleMonth(mo)}
                      style={{ borderColor: 'var(--border-color)',
                               background: isOpen ? 'var(--card-bg-alt)' : 'var(--card-bg)' }}
                      data-testid={`month-header-${mo}`}>
                    <td className="p-1.5 sticky left-0 z-10 whitespace-nowrap"
                        style={{ background: isOpen ? 'var(--card-bg-alt)' : 'var(--card-bg)', color: 'var(--text-primary)' }}>
                      <span className="inline-flex items-center gap-1 font-semibold">
                        {isOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                        <span className="font-mono">{mo}</span>
                        <span className="text-[9px]" style={{ color: 'var(--text-muted)' }}>({dayCount}d)</span>
                      </span>
                    </td>
                    {columns.map((c) => {
                      const v = monthTotals[mo]?.[c.key] || 0;
                      return (
                        <td key={c.key} className="p-1.5 text-right font-mono border-l"
                            style={{ color: v > 0 ? c.color : 'var(--text-disabled)',
                                     borderColor: 'var(--border-color)', fontWeight: 500 }}>
                          {fmt(v, c.dp)}
                        </td>
                      );
                    })}
                  </tr>
                  {/* Expanded daily rows */}
                  {isOpen && (monthGroups[mo] || []).map((d) => (
                    <tr key={d} className="border-t" style={{ borderColor: 'var(--border-color)' }}
                        data-testid={`day-row-${d}`}>
                      <td className="p-1 pl-6 sticky left-0 z-10 font-mono whitespace-nowrap"
                          style={{ background: 'var(--card-bg)', color: 'var(--text-muted)' }}>
                        {d}
                      </td>
                      {columns.map((c) => {
                        const v = c.values.get(d) || 0;
                        return (
                          <td key={c.key} className="p-1 text-right font-mono border-l"
                              style={{ color: v > 0 ? c.color : 'var(--text-disabled)', borderColor: 'var(--border-color)' }}>
                            {v > 0 ? v.toFixed(c.dp) : '·'}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="px-3 py-1.5 border-t flex flex-wrap items-center gap-3 text-[9px]"
           style={{ borderColor: 'var(--border-color)', color: 'var(--text-muted)', background: 'var(--card-bg-alt)' }}>
        <span className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full inline-block" style={{ background: '#3b82f6' }} /> Plan</span>
        <span className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full inline-block" style={{ background: '#f59e0b' }} /> Forecast</span>
        <span className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full inline-block" style={{ background: '#10b981' }} /> Actual</span>
        <span className="ml-2 inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full inline-block" style={{ background: '#7c3aed' }} /> EQ Plan</span>
        <span className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full inline-block" style={{ background: '#c084fc' }} /> EQ Fcst</span>
        <span className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full inline-block" style={{ background: '#a855f7' }} /> EQ Actl</span>
        <span className="ml-auto">MP/EQ head-counts are distributed across Qty dates proportionally to each day's Plan / Fcst / Actl share</span>
      </div>
    </div>
  );
}
